import type { Lead } from '@up2stay/up2promo-api-client';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';
import { Badge, Button, cn } from '@up2stay/up2promo-ui';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { ChevronDown, Loader2, Mail, Phone } from 'lucide-react';
import { useMemo, useState } from 'react';

import { LeadStatusBadge } from '@/core/components/leads/lead-detail/lead-status-badge';
import type { LeadDuplicateMatchReason } from '@/core/lib/find-lead-duplicates';

export type LeadDuplicateResolutionAction =
  | 'use'
  | 'supplement'
  | 'replace'
  | 'create';

export interface LeadDuplicateResolutionLabels {
  title: string;
  description: string;
  selectLead: string;
  matchReason: Record<LeadDuplicateMatchReason, string>;
  chooseAction: string;
  useExisting: string;
  useExistingDescription: string;
  supplement: string;
  supplementDescription: string;
  replace: string;
  replaceDescription: string;
  createNew: string;
  createNewDescription: string;
  moreOptions: string;
  continue: string;
  cancel: string;
  /** @deprecated Prefer cancel — kept for older call sites. */
  discard?: string;
  processing: string;
}

interface LeadDuplicateResolutionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matches: Array<{ lead: Lead; reasons: LeadDuplicateMatchReason[] }>;
  labels: LeadDuplicateResolutionLabels;
  getStatusLabel: (status: string) => string;
  isProcessing: boolean;
  onSupplement: (leadId: string) => void;
  onReplace: (leadId: string) => void;
  onDiscard: () => void;
  /** Keep the selected lead as-is (e.g. finish exchange without rewriting fields). */
  onUseExisting?: (leadId: string) => void;
  /** Create a separate lead / allow a deliberate duplicate. */
  onCreateNew?: () => void;
  /** Hide “use existing” when the flow always mutates or creates. */
  showUseExisting?: boolean;
  /** Hide “create new” when the API cannot force a duplicate. */
  showCreateNew?: boolean;
}

export function LeadDuplicateResolutionDialog({
  open,
  onOpenChange,
  matches,
  labels,
  getStatusLabel,
  isProcessing,
  onSupplement,
  onReplace,
  onDiscard,
  onUseExisting,
  onCreateNew,
  showUseExisting = Boolean(onUseExisting),
  showCreateNew = Boolean(onCreateNew),
}: LeadDuplicateResolutionDialogProps) {
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [syncKey, setSyncKey] = useState(() => ({
    open: false,
    matchKey: '',
  }));

  const matchKey = matches.map((match) => match.lead.id).join(',');
  if (open !== syncKey.open || (open && matchKey !== syncKey.matchKey)) {
    setSyncKey({ open, matchKey });
    if (open) {
      setSelectedLeadId(matches[0]?.lead.id ?? null);
      setShowAdvanced(false);
    }
  }

  const effectiveSelectedLeadId = useMemo(() => {
    if (
      selectedLeadId &&
      matches.some((match) => match.lead.id === selectedLeadId)
    ) {
      return selectedLeadId;
    }
    return matches[0]?.lead.id ?? null;
  }, [matches, selectedLeadId]);

  const selectedLead = matches.find(
    (m) => m.lead.id === effectiveSelectedLeadId,
  )?.lead;

  const runWithSelectedLead = (action: (leadId: string) => void): void => {
    if (!selectedLead) return;
    action(selectedLead.id);
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent
        className="flex max-h-[min(90dvh,40rem)] w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-lg"
        fitContent
        scrollable={false}
      >
        <ResponsiveDialogHeader className="shrink-0 space-y-1 px-4 pt-4 text-left sm:px-6 sm:pt-6">
          <ResponsiveDialogTitle>{labels.title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {labels.description}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4 sm:px-6">
          {matches.length > 1 ? (
            <p className="text-sm font-medium text-foreground">
              {labels.selectLead}
            </p>
          ) : null}

          <div className="flex flex-col gap-2">
            {matches.map(({ lead, reasons }) => {
              const isSelected = lead.id === effectiveSelectedLeadId;
              const selectable = matches.length > 1;
              return (
                <button
                  key={lead.id}
                  type="button"
                  disabled={isProcessing || !selectable}
                  onClick={() => selectable && setSelectedLeadId(lead.id)}
                  className={cn(
                    'rounded-lg border p-3 text-left transition-colors',
                    isSelected
                      ? 'border-primary/40 bg-muted/30'
                      : 'border-border',
                    selectable &&
                      !isSelected &&
                      'hover:border-primary/40 cursor-pointer',
                    !selectable && 'cursor-default',
                  )}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-foreground">
                      {formatLeadDisplayName(lead.firstName, lead.lastName) ||
                        lead.email ||
                        '—'}
                    </span>
                    <LeadStatusBadge
                      status={lead.status}
                      label={getStatusLabel(lead.status)}
                    />
                  </div>

                  {lead.companyName ? (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {lead.companyName}
                    </p>
                  ) : null}

                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {reasons.map((reason) => (
                      <Badge
                        key={reason}
                        variant="secondary"
                        className="rounded-full border-0 bg-amber-500/15 text-[11px] font-semibold text-amber-900 dark:text-amber-200"
                      >
                        {labels.matchReason[reason]}
                      </Badge>
                    ))}
                  </div>

                  <div className="mt-2 flex flex-col gap-1 text-sm text-muted-foreground">
                    {lead.email ? (
                      <div className="flex min-w-0 items-center gap-2">
                        <Mail className="h-3 w-3 shrink-0" />
                        <span className="truncate">{lead.email}</span>
                      </div>
                    ) : null}
                    {lead.phone ? (
                      <div className="flex min-w-0 items-center gap-2">
                        <Phone className="h-3 w-3 shrink-0" />
                        <span className="truncate">{lead.phone}</span>
                      </div>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>

          {showAdvanced ? (
            <div className="space-y-2 border-t pt-3">
              <p className="text-sm font-medium text-foreground">
                {labels.chooseAction}
              </p>
              <Button
                type="button"
                variant="outline"
                className="h-auto w-full justify-start px-3 py-2.5 text-left"
                disabled={isProcessing || !selectedLead}
                onClick={() => runWithSelectedLead(onReplace)}
              >
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm font-medium">{labels.replace}</span>
                  <span className="text-xs font-normal text-muted-foreground">
                    {labels.replaceDescription}
                  </span>
                </span>
              </Button>
              {showCreateNew ? (
                <Button
                  type="button"
                  variant="outline"
                  className="h-auto w-full justify-start px-3 py-2.5 text-left"
                  disabled={isProcessing || !onCreateNew}
                  onClick={() => onCreateNew?.()}
                >
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-sm font-medium">
                      {labels.createNew}
                    </span>
                    <span className="text-xs font-normal text-muted-foreground">
                      {labels.createNewDescription}
                    </span>
                  </span>
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Merge is the recommended way out; creating a duplicate anyway
            sits under "more options" (DEV-567). */}
        <div className="flex shrink-0 flex-col gap-2 border-t px-4 py-4 sm:px-6">
          <Button
            type="button"
            className="w-full"
            disabled={isProcessing || !selectedLead}
            onClick={() => runWithSelectedLead(onSupplement)}
          >
            {isProcessing ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                {labels.processing}
              </>
            ) : (
              labels.supplement
            )}
          </Button>

          {showUseExisting ? (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={isProcessing || !selectedLead || !onUseExisting}
              onClick={() => selectedLead && onUseExisting?.(selectedLead.id)}
            >
              {labels.useExisting}
            </Button>
          ) : null}

          <Button
            type="button"
            variant="ghost"
            className="w-full text-muted-foreground"
            disabled={isProcessing}
            onClick={() => setShowAdvanced((current) => !current)}
          >
            <ChevronDown
              className={cn(
                'size-4 transition-transform',
                showAdvanced && 'rotate-180',
              )}
              aria-hidden
            />
            {labels.moreOptions}
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            disabled={isProcessing}
            onClick={onDiscard}
          >
            {labels.cancel || labels.discard || 'Cancel'}
          </Button>
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
